import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../context/darkModeContext";
import useAuth from "../stores/authStore";
import { subscriptionBlocksAccess } from "./ProtectedRoute";
import NotificationPanel from "./NotificationPanel";
import secureLocalStorage from "react-secure-storage";
import { api } from "../utils/axios-instance";
import { auth } from "../helper/firebaseClient";
import { fileIcons } from "../utils/icon-mimetypes";
import { Dialog, DialogContent } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import FileView from "../pages/FileViewPage";
import fileContext from "../context/fileContext";
import { useContext } from "react";
import FileBg from "../generators/fileBGGenerator";
import toast from "react-hot-toast";

// images from public folder
const DarkMode = `${process.env.PUBLIC_URL}/assets/darkMode.svg`;
const LightMode = `${process.env.PUBLIC_URL}/assets/lightMode.svg`;
const DefaultIcon = `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;

/* ── Inline SVG icons ── */
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5" />
    <line
      x1="12.5"
      y1="12.5"
      x2="16"
      y2="16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 2a5.5 5.5 0 00-5.5 5.5v3L2 13h14l-1.5-2.5v-3A5.5 5.5 0 009 2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 15a1.5 1.5 0 003 0"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="3.5" stroke="#000000" strokeWidth="1.5" />
    <line
      x1="9"
      y1="1.5"
      x2="9"
      y2="3.5"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="9"
      y1="14.5"
      x2="9"
      y2="16.5"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="1.5"
      y1="9"
      x2="3.5"
      y2="9"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="14.5"
      y1="9"
      x2="16.5"
      y2="9"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="3.6"
      y1="3.6"
      x2="5"
      y2="5"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="13"
      y1="13"
      x2="14.4"
      y2="14.4"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="14.4"
      y1="3.6"
      x2="13"
      y2="5"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="5"
      y1="13"
      x2="3.6"
      y2="14.4"
      stroke="#000000"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M15 11A7 7 0 017 3a7 7 0 000 13 7 7 0 008-5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="currentColor"
      fillOpacity="0.12"
    />
  </svg>
);

const TopBar = () => {
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const token = useAuth((state) => state.token);
  const profileData = useAuth((state) => state.profileData);

  // Search state — lives here so the input is in the TopBar itself
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const hideTopBar =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/onboard" ||
    location.pathname === "/signup" ||
    location.pathname === "/create-organization" ||
    location.pathname === "/change-password" ||
    location.pathname === "/onboarding" ||
    location.pathname === "/waiting-lobby" ||
    location.pathname === "/contact-us" ||
    location.pathname === "/privacy-policy" ||
    location.pathname.startsWith("/ai") ||
    location.pathname === "/chat" ||
    // expired / unpaid: no search, notifications or navigation
    subscriptionBlocksAccess(profileData);

  if (hideTopBar || !token) return null;

  /* icon button shared style */
  const iconBtn = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 8,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: darkMode ? "#8b8faa" : "#6b7280",
    transition: "background 0.15s, color 0.15s",
    flexShrink: 0,
  };

  return (
    <>
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 20px",
          background: darkMode ? "#111827" : "#f1f1ff",
          borderBottom: "none",
          height: 56,
          boxSizing: "border-box",
        }}
      >
        {/* ── Search bar ── */}
        {/* <div
          onClick={() => setSearchOpen(true)}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: darkMode ? "#1e2037" : "#ffffff",
            border: `1px solid ${darkMode ? "#2a2d4a" : "#e5e7eb"}`,
            borderRadius: 8,
            padding: "7px 12px",
            maxWidth: 500,
            cursor: "text",
          }}
        >
          <SearchIcon />
          <span
            style={{
              flex: 1,
              fontSize: 13,
              color: "#9ca3af",
              userSelect: "none",
              fontFamily: "inherit",
            }}
          >
            Search files (contents too — OCR + text)…
          </span>
          <span
            style={{
              fontSize: 11,
              color: "#9ca3af",
              background: darkMode ? "#252742" : "#f3f4f6",
              border: `1px solid ${darkMode ? "#2a2d4a" : "#e5e7eb"}`,
              borderRadius: 4,
              padding: "1px 5px",
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            ⌘/
          </span>
        </div> */}

        {/* ── Right actions ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginLeft: "auto",
          }}
        >
          {/* Search */}
          <button
            style={iconBtn}
            onClick={() => setSearchOpen(true)}
            title="Search"
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = darkMode
                ? "#252742"
                : "#e5e7eb")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <SearchIcon />
          </button>

          {/* Dark mode toggle */}
          {/* <button
            style={iconBtn}
            onClick={toggleDarkMode}
            title={darkMode ? "Light mode" : "Dark mode"}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = darkMode
                ? "#252742"
                : "#e5e7eb")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            {darkMode ? <MoonIcon /> : <SunIcon />}
          </button> */}

          {/* Notifications */}
          <div style={{ position: "relative" }}>
            <NotificationPanel darkMode={darkMode} iconOnly />
          </div>
        </div>
      </nav>

      {/* Full search dialog — opens when search bar is clicked */}
      {searchOpen && (
        <SearchDialog
          open={searchOpen}
          closeDialog={() => {
            setSearchOpen(false);
            setSearchTerm("");
          }}
          darkMode={darkMode}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   SearchDialog — full-screen dialog (same logic as old SearchBar/Search)
───────────────────────────────────────────────────────────────────────── */
function SearchDialog({ open, closeDialog, darkMode }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [filesWithTerm, setFilesWithTerm] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFileViewOpen, setIsFileViewOpen] = useState(false);
  const [selectedFileInfo, setSelectedFileInfo] = useState({});
  const [sharedFileInfo, setSharedFileInfo] = useState({});

  const profileData = useAuth((state) => state.profileData);
  const formatFileSize = useAuth((state) => state.formatFileSize);
  const { allFiles } = useContext(fileContext);

  /* load users once */
  useEffect(() => {
    api
      .get("/users/list_users")
      .then((r) => setUsers(r.data))
      .catch(console.log);
  }, []);

  /* filter cached files by name */
  useEffect(() => {
    const cached = JSON.parse(
      secureLocalStorage.getItem("recentFilesCache") || "null",
    );
    if (cached) {
      setFilteredFiles(
        cached.filter((f) =>
          f?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }
  }, [searchTerm]);

  /* filter users by name */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchResults(
      users.filter((u) =>
        u?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, users]);

  const openDrawer = (file) => {
    api
      .get(`/file/sharedFileInfo/${file.id}`)
      .then((r) => setSharedFileInfo(r.data))
      .catch(console.log);
    setSelectedFileInfo({
      name: file.name,
      size: file.size,
      id: file.id,
      owner: file.owner,
      ownerProfileUrl: file.profilePic,
      lastUpdate: file.lastUpdate,
      mimetype: file.mimetype,
    });
    setIsFileViewOpen(true);
    setSearchTerm("");
  };

  const closeDrawer = () => {
    setIsFileViewOpen(false);
    closeDialog();
  };

  const handleTextSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const r = await api.get("/file/getFilesWithTerm/", {
        params: { searchTerm, org_id: profileData.org_id },
      });
      setFilesWithTerm(r?.data?.files || []);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      if (status === 429 && data) {
        toast.error(`${data.message} (${data.used}/${data.limit} used)`);
      } else {
        toast.error("Something went wrong while searching. Please try again.");
        console.log(e?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getIconByMimeType = (mime) => fileIcons[mime] || DefaultIcon;

  const FileCard = ({ file, type }) => (
    <div
      style={{
        borderRadius: 16,
        cursor: "pointer",
        padding: 12,
        border: "1px solid #ffa9ff46",
        backgroundImage: FileBg({
          gradientColors: [file.color, file.color, file.color, file.color],
        }),
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: "lighten",
      }}
      onClick={() => {
        let f = file;
        if (type === "term") f = allFiles.find((x) => x.id === file.id) || file;
        openDrawer(f);
      }}
    >
      <span
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          src={getIconByMimeType(file?.mimetype)}
          alt="file"
          style={{ borderRadius: 6, margin: "16px 0" }}
        />
      </span>
      <h5
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "0 0 4px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file?.name?.split("_TS=")[0]}
      </h5>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: "#676767" }}>
          {type === "term" ? formatFileSize(file?.size) : file.size}
        </span>
        <Avatar
          src={file?.profilePic}
          alt="owner"
          sx={{ width: 24, height: 24, border: "1px solid silver" }}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      PaperProps={{
        style: { borderRadius: 8, width: "80%", height: "90%", padding: 0 },
      }}
      maxWidth="lg"
    >
      <DialogContent
        style={{ position: "relative", height: "100%", padding: "16px 20px" }}
      >
        {/* Search input row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: darkMode ? "#1e2037" : "#fff",
              border: `1px solid ${darkMode ? "#2a2d4a" : "#e5e7eb"}`,
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            <SearchIcon />
            <input
              autoFocus
              type="search"
              placeholder="Search files (contents too — OCR + text)…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setFilesWithTerm([]);
              }}
              onKeyDown={(e) => e.key === "Backspace" && setFilesWithTerm([])}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 13,
                color: darkMode ? "#e8eaf0" : "#374151",
                background: "transparent",
                fontFamily: "inherit",
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#9ca3af",
                  padding: "2px 4px",
                  borderRadius: 4,
                }}
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={handleTextSearch}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              fontSize: 13,
              fontFamily: "inherit",
              cursor: "pointer",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Search Text
          </button>
        </div>

        {/* Results */}
        {searchTerm && (
          <div style={{ overflow: "auto", height: "calc(100% - 52px)" }}>
            {filteredFiles.length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "14px 4px 8px",
                    color: "#374151",
                  }}
                >
                  Files:
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 220px))",
                    gap: 12,
                  }}
                >
                  {filteredFiles.map((f) => (
                    <FileCard key={f.id} file={f} type="name" />
                  ))}
                </div>
              </>
            )}

            {(filesWithTerm.length > 0 || loading) && (
              <>
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "14px 4px 8px",
                    color: "#374151",
                  }}
                >
                  Files With Term:
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 220px))",
                    gap: 12,
                    position: "relative",
                    minHeight: loading ? 80 : 0,
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={36}
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: 16,
                        transform: "translateX(-50%)",
                      }}
                    />
                  ) : (
                    filesWithTerm.map((f) => (
                      <FileCard key={f.id} file={f} type="term" />
                    ))
                  )}
                </div>
              </>
            )}

            {searchResults.length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "14px 4px 8px",
                    color: "#374151",
                  }}
                >
                  Users:
                </h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        width: 80,
                      }}
                      onClick={() => {
                        navigate(`/user-profile/${user.id}`);
                        closeDialog();
                      }}
                    >
                      <Avatar
                        src={user.profilePictureUrl}
                        alt={user.name}
                        sx={{
                          width: 72,
                          height: 72,
                          border: "1px solid silver",
                        }}
                      />
                      <span
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "center",
                          color: "#374151",
                        }}
                      >
                        {user.name}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {isFileViewOpen && (
          <FileView
            fileInfo={selectedFileInfo}
            closeDrawer={closeDrawer}
            sharedFileInfo={sharedFileInfo}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TopBar;
